import { useMemo, useState } from 'react';

import { AgentsHeader } from './AgentsHeader/AgentsHeader.container';
import { AgentsListBodySection } from './AgentsListBodySection/AgentsListBody.section';
import { CreateAgentButton } from './CreateAgentButton.container';

import {
  Banner,
  BannerButton,
  BannerHeading,
  BannerIcon,
  BannerParagraph,
  BannerSuffix,
  Grid,
  OrderDirection,
  OrderInput,
  ThemeProvider,
} from '@aircall/tractor-v2';
import { BulkUserCreation } from '@components/BulkUserCreation';
import { DownloadExtract } from '@components/DownloadExtract';
import { FFEnabled } from '@components/FFEnabled';
import { DEFAULT_AGENTS_SORT_FIELD } from '@constants/generic.constants';
import {
  ACCOUNT_ROUTE_TAB,
  ACCOUNT_TABS,
  USER_DETAIL_ROUTE_TAB,
} from '@constants/routes.constants';
import {
  useToggle,
  useLoadMore,
  Gap,
  PageHeader,
  LIST_VIEW_PARAMS,
  useListViewFilters,
} from '@dashboard/library';
import { GenerateAgentsExtractMutation } from '@generated/GenerateAgentsExtractMutation';
import { SearchAgentsQuery, SearchAgentsQueryVariables } from '@generated/SearchAgentsQuery';
import { GENERATE_AGENTS_EXTRACT_MUTATION } from '@graphql/mutations/GenerateAgentsExtractMutation';
import { SEARCH_AGENTS_QUERY } from '@graphql/queries/SearchAgentsQuery';
import { getSearchAgentsFilter } from '@helpers/search.helpers';
import { getSearchAgentsSort } from '@helpers/sort.helpers';
import { useNavigateWithParamsReplace } from '@hooks/useNavigateWithParamsReplace';
import { usePermissions } from '@hooks/usePermissions';
import { useTranslation } from 'react-i18next';

export function getTotal(data: SearchAgentsQuery | null | undefined): number {
  return data?.searchAgents?.total ?? 0;
}

export function AgentsListPage() {
  const { t } = useTranslation();
  const { enablePermissionsNotifications } = usePermissions();
  const [userIdToNavigate, setUserIdToNavigate] = useState<string | undefined>(undefined);
  const [isBulkUsersOpen, _, openBulkUsers, closeBulkUsers] = useToggle(false);
  const navigate = useNavigateWithParamsReplace();
  const { get, setMultiple } = useListViewFilters();

  const sortField = get(LIST_VIEW_PARAMS.SORT_FIELD, DEFAULT_AGENTS_SORT_FIELD);
  const sortDirection = get(LIST_VIEW_PARAMS.SORT_DIRECTION, OrderDirection.Asc);
  const searchQuery = get(LIST_VIEW_PARAMS.SEARCH);
  const teamsIds = get(LIST_VIEW_PARAMS.TEAMS);
  const roles = get(LIST_VIEW_PARAMS.ROLES);
  const availability = get(LIST_VIEW_PARAMS.AVAILABILITY);

  const tableOrder: OrderInput = { field: sortField, direction: sortDirection };

  // Filter should be memoized otherwise
  // useLoadMore will reset "from" variable to 0
  const filter = useMemo(
    () => getSearchAgentsFilter({ searchQuery, availability, roles, teamsIds }),
    // We pass dependencies arrays as strings to avoid filter to change reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchQuery, availability?.toString(), roles?.toString(), teamsIds?.toString()]
  );

  const sort = useMemo(
    () => getSearchAgentsSort({ direction: sortDirection, field: sortField }),
    [sortDirection, sortField]
  );

  const { loading, data, staleData, hasMore, loadMore } = useLoadMore<
    SearchAgentsQuery,
    SearchAgentsQueryVariables
  >({
    query: SEARCH_AGENTS_QUERY,
    queryOptions: {
      variables: {
        filter,
        sort,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
    getTotal,
  });

  const handleNavigateToNewUser = () =>
    userIdToNavigate &&
    navigate(USER_DETAIL_ROUTE_TAB, { userId: userIdToNavigate, tab: 'general' });

  const onOrderChange = (field: OrderInput['field'], direction: OrderDirection) => {
    setMultiple({
      [LIST_VIEW_PARAMS.SORT_FIELD]: field,
      [LIST_VIEW_PARAMS.SORT_DIRECTION]: direction,
    });
  };

  return (
    <ThemeProvider>
      {isBulkUsersOpen && <BulkUserCreation onClose={closeBulkUsers} />}

      <Grid data-test='agents-list-section' gridAutoFlow='row' gridTemplateRows='auto 1fr' h='100%'>
        <PageHeader
          largeTitle={t('users.title')}
          titleProps={{ justifyContent: 'space-between' }}
          renderTitleRight={() => (
            <Gap gap='s'>
              <CreateAgentButton
                onCreateMultipleUsers={openBulkUsers}
                size='small'
                onCreationCompleted={handleNavigateToNewUser}
                onCreationSubmited={setUserIdToNavigate}
              />
              <FFEnabled featureFlag={(featureFlags) => featureFlags.dashboardExtractReport}>
                <DownloadExtract<GenerateAgentsExtractMutation>
                  mutation={GENERATE_AGENTS_EXTRACT_MUTATION}
                  getUrl={
                    /* istanbul ignore next */
                    (d) => d.generateAgentsExtract.extractFileUrl
                  }
                />
              </FFEnabled>
            </Gap>
          )}
        />

        {enablePermissionsNotifications && (
          <Banner data-test='agents-permissions-banner' variant='info'>
            <BannerIcon alignSelf='flex-start' />
            <BannerHeading>{t('users.permissions_banner.text')}</BannerHeading>
            <BannerParagraph>{t('users.permissions_banner.subtext')}</BannerParagraph>
            <BannerSuffix>
              <BannerButton
                mode='link'
                onClick={() => navigate(ACCOUNT_ROUTE_TAB, { tab: ACCOUNT_TABS.PERMISSIONS })}
                data-test='agents-permissions-banner-button'
              >
                {t('generic.learn_more')}
              </BannerButton>
            </BannerSuffix>
          </Banner>
        )}

        <Gap flexDirection='column' gap='s' p='xl' py='m' w='100%' overflow='hidden'>
          <AgentsHeader searchQuery={searchQuery} agentsTotal={data?.searchAgents?.total} />

          <AgentsListBodySection
            agentsList={staleData?.searchAgents?.items}
            hasMore={hasMore}
            loading={loading && !data}
            loadingMore={loading && !!staleData}
            onLoadMore={loadMore}
            onOrderChange={onOrderChange}
            order={tableOrder}
          />
        </Gap>
      </Grid>
    </ThemeProvider>
  );
}
